import { createSelector } from '@reduxjs/toolkit'
import { compareByName } from '../../app/util/customerOptions.util'

const EMPTY_ALL_CUSTOMERS = [] // so it doesn't change reference if no data in selectAllCustomers

const get = ({ customers }) => customers

export const getSelectedCustomer = createSelector(get, (customers) => customers.selectedCustomer)

export const getCustomersSyncing = createSelector(get, (customers) => customers.syncing ?? [])

export const getSelectedCustomerId = createSelector(getSelectedCustomer, (selectedCustomer) => selectedCustomer?.id)

export const getIsNoSelectedCustomer = createSelector(
  getSelectedCustomerId,
  (selectedCustomerId) => !selectedCustomerId,
)

export const getSelectedCustomerName = createSelector(
  getSelectedCustomer,
  (selectedCustomer) => selectedCustomer?.customer_name,
)

export const getSelectedCustomerAppliedTemplate = createSelector(
  getSelectedCustomer,
  (selectedCustomer) => selectedCustomer?.applied_template,
)

export const getIsParentCompanySelected = createSelector(
  getSelectedCustomer,
  (selectedCustomer) => selectedCustomer?.parent_id === null,
)

export const getIsCspChild = createSelector(getSelectedCustomer, (selectedCustomer) => selectedCustomer?.hasCspMapping)

export const getIsSyncingData = createSelector(getCustomersSyncing, getSelectedCustomerId, (syncing, customerId) =>
  syncing.some((id) => id === customerId),
)

export const getCustomers = createSelector(get, (customers) => customers.data ?? EMPTY_ALL_CUSTOMERS)

export const getCustomersCount = createSelector(getCustomers, (customers) => customers.length ?? 0)

export const getIsNoCustomers = createSelector(getCustomersCount, (customersCount) => customersCount === 0)

export const getFirstCustomer = createSelector(getCustomers, (customers) => customers[0])

export const getFirstCustomerId = createSelector(getFirstCustomer, (firstCustomer) => firstCustomer?.id)

export const getParentCustomer = createSelector(getCustomers, (customers) =>
  customers.find((customer) => !customer.parent_id),
)

export const getParentCustomerId = createSelector(getParentCustomer, (parentCustomer) => parentCustomer?.id)

export const getSelectedOrFirstCustomer = createSelector(
  getSelectedCustomer,
  getFirstCustomer,
  (selectedCustomer, firstCustomer) => selectedCustomer || firstCustomer,
)

export const getSelectedOrFirstCustomerId = createSelector(
  getSelectedCustomerId,
  getFirstCustomerId,
  (selectedCustomerId, firstCustomerId) => selectedCustomerId || firstCustomerId,
)

// if data is being derived (transformed) from another selector memoize selector using `createSelector`
export const getM365Customers = createSelector(
  getCustomers, // it will only recalculate if selectAllCustomers result change
  (allCustomers) => allCustomers.filter((customer) => customer?.integratedApplications?.includes('Office365')),
)

export const getCustomerOptions = createSelector(getCustomers, (customers) =>
  customers
    .map((item) => ({ label: item.customer_name, value: item.id }))
    .sort((a, b) => compareByName(a.label, b.label)),
)

export const getM365CustomerOptions = createSelector(getM365Customers, (m365Customers) =>
  m365Customers
    .map((item) => ({ label: item.customer_name, value: item.id }))
    .sort((a, b) => compareByName(a.label, b.label)),
)

// map of id -> customer
export const getCustomersMap = createSelector(getCustomers, (allCustomers) =>
  allCustomers.reduce((acc, customer) => {
    acc[customer.id] = customer
    return acc
  }, {}),
)
